import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';
import type { ConnectorStatus } from '@/entities/media-connector';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover/FilterChipPopover';
import { SearchInput } from '@/shared/ui/SearchInput/SearchInput';
import { i18n } from '@/shared/i18n';
import type { MediaConnectorToolbarProps } from './MediaConnectorToolbar.types';

// Getters, not values: resolving per read is what lets the labels follow a language change.
const STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: ConnectorStatus; label: string }> = [
  {
    value: 'connected',
    get label() {
      return i18n.t('knowledge:mediaConnector.types.status.connected');
    },
  },
  {
    value: 'disconnected',
    get label() {
      return i18n.t('knowledge:mediaConnector.types.status.disconnected');
    },
  },
  {
    value: 'expired',
    get label() {
      return i18n.t('knowledge:mediaConnector.types.status.expired');
    },
  },
  {
    value: 'error',
    get label() {
      return i18n.t('knowledge:mediaConnector.types.status.error');
    },
  },
  {
    value: 'pending',
    get label() {
      return i18n.t('knowledge:mediaConnector.types.status.pending');
    },
  },
];

export function MediaConnectorToolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onAddAccount,
  isConnecting,
}: MediaConnectorToolbarProps) {
  const { t } = useTranslation('knowledge');
  const searchLabel = t('mediaConnectors.mediaConnectorDetail.searchAccountsLabel');

  return (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="flex items-center gap-2">
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder={t('mediaConnectors.mediaConnectorDetail.searchAccounts')}
          aria-label={searchLabel}
          className="w-80"
          size="sm"
        />
        <FilterChipPopover
          label={t('mediaConnectors.mediaConnectorDetail.status')}
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={onStatusFilterChange}
        />
      </div>
      <Button size="default" onClick={onAddAccount} disabled={isConnecting}>
        {isConnecting ? (
          <Spinner className="mr-1.5" aria-hidden />
        ) : (
          <Plus className="mr-1.5 size-4" />
        )}
        {t('mediaConnectors.mediaConnectorDetail.addAccount')}
      </Button>
    </div>
  );
}

MediaConnectorToolbar.displayName = 'MediaConnectorToolbar';
